<template>
  <RootPageLayout
    id="support"
    class="help"
    title="Support Request and FAQ"
    mobileTitle="Support"
  >
    <div v-if="isOffline()" class="layout__wrapper-inner container">
      <ContentWrapper>
        <p>
          It looks like you’re offline. Please connect to the Internet and try
          again.
        </p>
        <p>
          Alternatively, you can send us an email directly at
          <a href="mailto:help@shortform.com">help@shortform.com</a>.
        </p>
      </ContentWrapper>
    </div>

    <div v-else class="layout__wrapper-inner container">
      <div class="help__subtitle">
        Check our
        <RouterLink
          to="/app/help#faq"
          class="help__faq-link"
          @click="scrollToFaq"
        >
          FAQ below
        </RouterLink>
        for common questions and solutions.<br />
        Didn’t find an answer to your question? Fill out the form below, or send
        us an email directly at
        <a href="mailto:help@shortform.com">help@shortform.com</a>
      </div>
      <div v-show="sentAlert" class="alert alert-success">
        Successfully sent a request.
      </div>
      <form role="form" class="form" novalidate @submit.prevent="send">
        <fieldset id="support-edit-form-group-subject" class="form__group">
          <div tabindex="-1" role="group">
            <label class="form__label form__label--large"> Subject </label>
            <InputWithContextMenu
              id="support-edit-subject"
              ref="subjectInput"
              v-model="subject"
              placeholder="Enter subject here"
              type="text"
              :class="validation.stateClass('subject')"
              class="form-control"
            />
            <FormError :errors="validation.errors" field="subject" />
          </div>
        </fieldset>
        <fieldset id="support-edit-form-group-body" class="form__group">
          <div tabindex="-1" role="group">
            <label class="form__label form__label--large">Message</label>
            <InputWithContextMenu
              id="support-edit-body"
              ref="bodyTextarea"
              v-model="body"
              elementType="textarea"
              placeholder="How can we help?"
              rows="8"
              type="text"
              :class="validation.stateClass('body')"
              class="form-control"
            />
            <FormError :errors="validation.errors" field="body" />
          </div>
        </fieldset>
        <div
          v-if="validation.errors && validation.errors._schema"
          class="invalid-feedback error-body"
        >
          {validation.errors._schema}
        </div>
        <div class="form__btns-wrapper">
          <button v-wait:disabled="'Sending'" type="submit" class="btns">
            <v-wait for="Sending">
              <template v-slot:waiting>
                Sending
                <button-spinner />
              </template>
              Send request
            </v-wait>
          </button>
          <a class="help__version" href="javascript:window.location.reload()">
            Version {{ APP_VERSION }}
          </a>
        </div>
      </form>
    </div>

    <SupportFAQ id="faq" />
  </RootPageLayout>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useWait } from '@/app/composables/useWait'

import RootPageLayout from '@/app/components/RootPageLayout.vue'
import ContentWrapper from '@/components/ui/ContentWrapper.vue'
import FormError from '@/components/ui/FormError.vue'
import ButtonSpinner from '@/components/ui/ButtonSpinner.vue'
import InputWithContextMenu from '@/app/components/InputWithContextMenu.vue'
import SupportFAQ from '@/app/components/faq/SupportFAQ.vue'

import { APP_VERSION } from '@/init/settings'
import { ValidationState, setInputFocusStart } from '@/helpers/form'
import { offline } from '@/services/offline'
import { backend } from '@/services/backend'
import { platform } from '@/services/platform'
import { processDevCommands } from '@/services/commands'
import { scrollIntoView } from '@/helpers/dom'

const router = useRouter()
const wait = useWait()

const subjectInput = ref<InstanceType<typeof InputWithContextMenu> | null>(null)

const validation = ref(new ValidationState())
const redirectDelay = ref(2000)

const sentAlert = ref(false)
const subject = ref('')
const body = ref('')

onMounted(async () => {
  if (offline.isOnline) {
    const input = subjectInput.value?.$el
    setInputFocusStart(input)

    const info = await platform.deviceInfo()
    if (window.__UNIT_TESTING !== true) {
      console.log(info)
    }
  }
  processDevCommands()
})

async function send(): Promise<void> {
  try {
    wait.start('Sending')
    await backend.sendSupportRequest(subject.value, body.value)
    validation.value.reset()

    // Show subscribed alert and redirect to books page after delay.
    sentAlert.value = true

    setTimeout(() => {
      wait.end('Sending')
      router.push({ name: 'discover' })
    }, redirectDelay.value)
  } catch (error) {
    validation.value.showErrors(error)
    wait.end('Sending')
  }
}

function isOffline(): boolean {
  return offline.isOffline
}

function scrollToFaq(): void {
  scrollIntoView('#faq', { behaviour: 'smooth' })
}
</script>

<style lang="scss" scoped>
.help {
  &__subtitle {
    font-size: 18px;
    font-weight: 400;
    line-height: 26px;
    color: $c-black-3;
    padding-bottom: 24px;
    border-bottom: 1px solid $c-border-7;

    a {
      color: $c-blue-3;
    }

    @media only screen and (max-width: $w-tablet-s) {
      padding-top: 30px;
    }
  }

  &__version {
    margin-top: 1rem;
    float: right;
  }

  &__faq-link {
    text-decoration: underline;
    color: $c-black-3 !important;
  }

  &__version {
    font-size: 16px;
    color: $c-black-6;
  }

  :deep(.page-title) {
    padding-top: 40px;
    padding-bottom: 16px;
    background-color: transparent;

    .container {
      max-width: 830px;
    }
  }

  .form {
    margin-top: 24px;

    &__group {
      margin-bottom: 16px;
    }

    &__btns-wrapper {
      padding-top: 12px;
    }

    input,
    textarea {
      border-radius: 8px;
    }
  }

  :deep(.page-title__title) {
    font-size: 32px;
  }

  &.layout :deep(.layout__wrapper) {
    padding-bottom: 0px;
  }

  .layout__wrapper-inner {
    padding-top: 0px;
    margin-bottom: 40px;
    gap: 0px;
  }

  .container {
    max-width: 830px;
  }

  :deep(.faq-list) {
    margin-bottom: 40px;
  }
}

.night-mode {
  .help {
    :deep(.page-title) {
      background-color: transparent;
    }

    &__subtitle {
      color: $c-black-4;
      border-bottom: 1px solid $c-black-3;
    }
  }
}
</style>
